// Portal › Kullanıcılar — "Davet Et" modalı (M16 T20, DR10): ad + e-posta (+ isteğe bağlı set
// erişimi). Hata tek genel mesaj (sunucu kodu gösterilmez). Stil ParticipantModals deseni.

import { useEffect, useId, useRef, useState } from "react";
import type {
  ClientAdminScope,
  ClientAdminSet,
  ClientEntity,
  InviteClientUserResponse,
} from "@mahir/shared";
import { inviteClientUser } from "../../api/client-admin";
import { CLIENT_ADMIN_COPY as C } from "../../lib/client-admin-copy";
import { describeAccessError } from "../../lib/client-access-errors";
import { draftToGrantV2, newSetDraftV2 } from "../../lib/client-access-draft-v2";

interface Props {
  clientId: string;
  sets: ClientAdminSet[];
  entities: ClientEntity[];
  /** Kısıtlı Admin kapsamı (yeni erişim kapsamla sınırlanır); aksi null. */
  scope: ClientAdminScope | null;
  open: boolean;
  onClose: () => void;
  onInvited: (result: InviteClientUserResponse) => void;
}

export function ClientUserInviteModal(props: Props): JSX.Element | null {
  const { clientId, sets, entities, scope, open, onClose, onInvited } = props;
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [selectedSets, setSelectedSets] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape" && !loading) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  function reset(): void {
    setEmail("");
    setFullName("");
    setSelectedSets(new Set());
    setError(null);
  }

  function handleClose(): void {
    if (loading) return;
    reset();
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      // Davetle verilen erişim: varsayılan taslak (tam erişim; kısıtlı Admin'de kapsamıyla sınırlı).
      // Rol/istisna gönderilmez → mevcut kullanıcının rolü ve istisnaları korunur.
      const grants = sets
        .filter((s) => selectedSets.has(s.id))
        .map((s) =>
          draftToGrantV2(newSetDraftV2(s, scope, entities), {
            lockedNullCategories: false,
            lockedNullEntities: false,
            omitRole: true,
            omitOverrides: true,
          }),
        );
      const result = await inviteClientUser(clientId, {
        email: email.trim(),
        fullName: fullName.trim(),
        ...(grants.length > 0 ? { grants } : {}),
      });
      reset();
      onInvited(result);
    } catch (err) {
      const info = describeAccessError(err, C.inviteModal.error);
      setError(info.text);
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = email.trim().length > 3 && fullName.trim().length > 1 && !loading;
  const inputStyle: React.CSSProperties = {
    borderColor: "var(--color-border)",
    fontFamily: "var(--font-sans)",
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="rounded-xl p-6 max-w-md w-full mx-4"
        style={{ background: "var(--color-surface)", color: "var(--color-text)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id={titleId}
          className="mb-1"
          style={{ fontFamily: "var(--font-display)", fontSize: "1.25rem" }}
        >
          {C.inviteModal.title}
        </h2>
        <p className="mb-4 text-sm" style={{ color: "var(--color-text-2)" }}>
          {C.inviteModal.description}
        </p>

        <label className="block text-xs mb-1" style={{ color: "var(--color-text-2)" }}>
          {C.inviteModal.fullName}
          <input
            ref={firstRef}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={loading}
            className="w-full p-2 rounded border text-sm mt-1 mb-3"
            style={inputStyle}
            placeholder={C.inviteModal.fullNamePlaceholder}
          />
        </label>

        <label className="block text-xs mb-1" style={{ color: "var(--color-text-2)" }}>
          {C.inviteModal.email}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className="w-full p-2 rounded border text-sm mt-1 mb-3"
            style={inputStyle}
            placeholder={C.inviteModal.emailPlaceholder}
          />
        </label>

        <fieldset className="mb-3">
          <legend className="text-xs mb-1" style={{ color: "var(--color-text-2)" }}>
            {C.inviteModal.sets}
          </legend>
          {sets.length === 0 ? (
            <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
              {C.inviteModal.noSets}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {sets.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedSets.has(s.id)}
                    disabled={loading}
                    onChange={(e) => {
                      const next = new Set(selectedSets);
                      if (e.target.checked) next.add(s.id);
                      else next.delete(s.id);
                      setSelectedSets(next);
                    }}
                  />
                  <span>{s.name}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {error && (
          <div
            role="alert"
            className="rounded-md p-3 mb-3 text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            {C.inviteModal.cancel}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {loading ? C.inviteModal.submitting : C.inviteModal.submit}
          </button>
        </div>
      </div>
    </div>
  );
}
